import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton,
  Link,
  MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import AppDialog from '../../../shared/components/AppDialog';
import { translateMessage } from '../../../shared/api';
import EmptyState from '../../../shared/components/EmptyState';
import { dataTableSx, tableActionCellSx } from '../../../shared/components/TableHeader';
import { triggerBrowserDownload } from '../../../shared/utils/download';
import { formatHistoryDateTime } from '../../../shared/utils/historyDateTime';
import { EMPTY_VALUE } from '../../../shared/utils/number';
import { HISTORY_ENTRY_TYPES } from '../model/dataInputModel';
import {
  HISTORY_SORT_DIRECTIONS,
  HISTORY_STATUS_ALL,
  filterAndSortHistory,
  paginateHistory,
} from '../model/historyTableModel';
import { getImportResultPresentation } from '../model/importResultPresentation';
import HistoryDetailDialog, {
  HistorySummary,
  historyEntryTitle,
} from './HistoryDetailPanel';

const ROWS_PER_PAGE = 10;

const STATUS_ORDER = [
  'QUEUED',
  'PROCESSING',
  'SUCCESS',
  'PARTIAL',
  'CANCELLED',
  'FAILED',
];

const historyStatusOptions = (entries) => {
  const statuses = new Set(
    entries
      .map((entry) => entry?.status)
      .filter(Boolean),
  );
  const orderedKnownStatuses = STATUS_ORDER.filter((status) => statuses.has(status));
  const unknownStatuses = [...statuses]
    .filter((status) => !STATUS_ORDER.includes(status))
    .sort();
  return [HISTORY_STATUS_ALL, ...orderedKnownStatuses, ...unknownStatuses];
};

const statusColor = (status) => {
  if (status === 'SUCCESS') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'PARTIAL' || status === 'QUEUED') return 'warning';
  if (status === 'CANCELLED') return 'default';
  if (status === 'PROCESSING') return 'info';
  return 'default';
};

const importOutcomeMessage = (entry, t) => {
  const presentation = getImportResultPresentation(entry);
  if (!presentation) return null;
  if (presentation.messageKey) return t(presentation.messageKey, presentation.params);
  return translateMessage(
    presentation.messageCode || 'IMPORT_PROCESSING_FAILED',
    500,
    presentation.params || entry.params,
  );
};

function StatusChip({ status }) {
  const { t } = useTranslation();
  if (!status) return <Typography variant="caption">{EMPTY_VALUE}</Typography>;
  return (
    <Chip
      size="small"
      label={t(`dataInput.operation.status.${status}`, { defaultValue: status })}
      color={statusColor(status)}
      sx={{ borderRadius: '6px', fontWeight: 700 }}
    />
  );
}

const isRemoteDetail = (entry) => (
  entry?.type === HISTORY_ENTRY_TYPES.SEAT_SYNC
  || entry?.type === HISTORY_ENTRY_TYPES.CAT8_SYNC
);

export default function UploadHistoryDialog({
  open, onClose, onExited, title, entries = [], onLoadDetail,
  canDownload = false, onDownload,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [statusFilter, setStatusFilter] = useState(HISTORY_STATUS_ALL);
  const [sortDirection, setSortDirection] = useState(HISTORY_SORT_DIRECTIONS.NEWEST);
  const [page, setPage] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const detailCache = useRef(new Map());
  const detailController = useRef(null);
  const detailSequence = useRef(0);
  const downloadController = useRef(null);

  const abortDownloadRequest = () => {
    downloadController.current?.abort();
    downloadController.current = null;
  };

  const abortDetailRequest = () => {
    detailSequence.current += 1;
    detailController.current?.abort();
    detailController.current = null;
  };

  const closeDetail = () => {
    abortDetailRequest();
    setDetailOpen(false);
  };

  const resetDetailPayload = () => {
    setSelectedEntry(null);
    setDetail(null);
    setDetailLoading(false);
    setDetailError('');
  };

  useEffect(() => {
    if (!open) return;
    abortDetailRequest();
    detailCache.current.clear();
    setStatusFilter(HISTORY_STATUS_ALL);
    setSortDirection(HISTORY_SORT_DIRECTIONS.NEWEST);
    setPage(0);
    setDetailOpen(false);
    setSelectedEntry(null);
    setDetail(null);
    setDetailLoading(false);
    setDetailError('');
    abortDownloadRequest();
    setDownloadingId(null);
    setDownloadError(null);
  }, [open, title]);

  useEffect(() => () => {
    abortDetailRequest();
    abortDownloadRequest();
  }, []);

  const filteredEntries = useMemo(
    () => filterAndSortHistory(entries, {
      status: statusFilter,
      direction: sortDirection,
    }),
    [entries, sortDirection, statusFilter],
  );
  const statusOptions = useMemo(() => historyStatusOptions(entries), [entries]);
  const visibleEntries = useMemo(
    () => paginateHistory(filteredEntries, page, ROWS_PER_PAGE),
    [filteredEntries, page],
  );

  useEffect(() => {
    if (statusFilter !== HISTORY_STATUS_ALL && !statusOptions.includes(statusFilter)) {
      setStatusFilter(HISTORY_STATUS_ALL);
      setPage(0);
    }
  }, [statusFilter, statusOptions]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredEntries.length / ROWS_PER_PAGE) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredEntries.length, page]);

  const loadRemoteDetail = async (entry, { force = false } = {}) => {
    if (!onLoadDetail) {
      setDetailError(t('dataInput.history.detailLoadError'));
      return;
    }
    const detailId = entry.snapshotId || entry.id;
    if (!force && detailCache.current.has(detailId)) {
      setDetail(detailCache.current.get(detailId));
      setDetailLoading(false);
      setDetailError('');
      return;
    }

    abortDetailRequest();
    const requestId = detailSequence.current;
    const controller = new AbortController();
    detailController.current = controller;
    setDetail(null);
    setDetailLoading(true);
    setDetailError('');
    try {
      const loadedDetail = await onLoadDetail(detailId, { signal: controller.signal });
      if (controller.signal.aborted || requestId !== detailSequence.current) return;
      detailCache.current.set(detailId, loadedDetail);
      setDetail(loadedDetail);
    } catch (error) {
      if (controller.signal.aborted || requestId !== detailSequence.current) return;
      setDetailError(t('dataInput.history.detailLoadError'));
    } finally {
      if (!controller.signal.aborted && requestId === detailSequence.current) {
        setDetailLoading(false);
        detailController.current = null;
      }
    }
  };

  const viewDetails = (entry) => {
    abortDetailRequest();
    setSelectedEntry(entry);
    setDetailOpen(true);
    setDetail(entry.detail);
    setDetailError('');
    setDetailLoading(false);
    if (isRemoteDetail(entry)) loadRemoteDetail(entry);
  };

  const retryDetail = () => {
    if (selectedEntry && isRemoteDetail(selectedEntry)) {
      loadRemoteDetail(selectedEntry, { force: true });
    }
  };

  const resetTableContext = () => {
    setPage(0);
    closeDetail();
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    resetTableContext();
  };

  const toggleSort = () => {
    setSortDirection((current) => (
      current === HISTORY_SORT_DIRECTIONS.NEWEST
        ? HISTORY_SORT_DIRECTIONS.OLDEST
        : HISTORY_SORT_DIRECTIONS.NEWEST
    ));
    resetTableContext();
  };

  const downloadImport = async (entry, file = null) => {
    if (!onDownload || downloadingId) return;
    abortDownloadRequest();
    const controller = new AbortController();
    downloadController.current = controller;
    const downloadId = file?.fileId ? `${entry.id}:${file.fileId}` : entry.id;
    setDownloadingId(downloadId);
    setDownloadError(null);
    try {
      const payload = await onDownload(entry.id, {
        signal: controller.signal,
        ...(file?.fileId ? { fileId: file.fileId } : {}),
      });
      if (controller.signal.aborted) return;
      triggerBrowserDownload({
        href: payload.download_url,
        fileName: payload.file_name || file?.fileName || entry.fileName || '',
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      setDownloadError({
        id: downloadId,
      });
    } finally {
      if (!controller.signal.aborted) {
        setDownloadingId(null);
        downloadController.current = null;
      }
    }
  };

  const handleClose = () => {
    closeDetail();
    abortDownloadRequest();
    onClose();
  };

  return (
    <>
      <AppDialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        TransitionProps={{ onExited }}
        sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100%', sm: 'calc(100vh - 48px)' },
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t('dataInput.history.title')}
          {title && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {title}
            </Typography>
          )}
          <IconButton
            onClick={handleClose}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', minHeight: 0, overflow: 'hidden', p: { xs: 2, sm: 2.5 } }}
        >
          {entries.length === 0 ? (
            <Box sx={{ display: 'grid', flex: 1, placeItems: 'center' }}>
              <EmptyState
                title={t('dataInput.history.empty')}
                description={t('dataInput.history.emptyHint')}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0, minWidth: 0, gap: 1.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('dataInput.history.filterStatus')}
                  </Typography>
                  <Select
                    size="small"
                    value={statusFilter}
                    onChange={handleStatusChange}
                    inputProps={{ 'aria-label': t('dataInput.history.filterStatus') }}
                    sx={{ minWidth: 172 }}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status === HISTORY_STATUS_ALL
                          ? t('dataInput.history.allStatuses')
                          : t(`dataInput.operation.status.${status}`)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SwapVertRoundedIcon fontSize="small" />}
                    onClick={toggleSort}
                    sx={{ display: { xs: 'inline-flex', sm: 'none' }, whiteSpace: 'nowrap' }}
                  >
                    {t(sortDirection === HISTORY_SORT_DIRECTIONS.NEWEST
                      ? 'dataInput.history.sortNewest'
                      : 'dataInput.history.sortOldest')}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('dataInput.history.resultCount', {
                    filtered: filteredEntries.length,
                    total: entries.length,
                  })}
                </Typography>
              </Stack>
              <Paper
                variant="outlined"
                sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
              >
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table
                    stickyHeader
                    size="small"
                    sx={(muiTheme) => ({
                      ...dataTableSx(muiTheme),
                      minWidth: { xs: 520, sm: 690, md: 920 },
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        textTransform: 'none',
                      },
                    })}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 152 }}>
                          <TableSortLabel
                            active
                            direction={sortDirection}
                            onClick={toggleSort}
                          >
                            {t('dataInput.history.column.date')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>{t('dataInput.history.column.event')}</TableCell>
                        <TableCell sx={{ width: 132 }}>{t('dataInput.history.column.status')}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: 150 }}>
                          {t('dataInput.history.column.actor')}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 190 }}>
                          {t('dataInput.history.column.summary')}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 116 }}>
                          {t('dataInput.history.column.actions')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleEntries.map((entry) => {
                        const actor = entry.createdByKey ? t(entry.createdByKey) : entry.createdBy;
                        const outcomeMessage = entry.type === HISTORY_ENTRY_TYPES.IMPORT
                          ? importOutcomeMessage(entry, t)
                          : null;
                        const showImportOutcome = ['FAILED', 'PARTIAL', 'CANCELLED']
                          .includes(entry.status);
                        return (
                          <TableRow
                            key={entry.id}
                            hover
                            selected={selectedEntry?.id === entry.id}
                          >
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              <Typography variant="caption">
                                {formatHistoryDateTime(entry.createdAt, i18n.language)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 190, verticalAlign: 'top' }}>
                              {entry.type === HISTORY_ENTRY_TYPES.IMPORT
                                && entry.downloadAvailable
                                && canDownload ? (
                                <Stack spacing={0.35}>
                                  {(entry.files?.length ? entry.files : [null]).map((file) => {
                                    const downloadId = file?.fileId ? `${entry.id}:${file.fileId}` : entry.id;
                                    return (
                                      <Stack key={downloadId} direction="row" alignItems="center" spacing={0.75}>
                                        <Link
                                          component="button"
                                          type="button"
                                          underline="always"
                                          onClick={() => downloadImport(entry, file)}
                                          disabled={Boolean(downloadingId)}
                                          aria-label={t('dataInput.history.downloadFile', {
                                            file: file?.fileName || historyEntryTitle(entry, t),
                                          })}
                                          sx={{
                                            color: 'primary.main', fontWeight: 700, textAlign: 'left',
                                            maxWidth: 320, overflowWrap: 'anywhere', cursor: 'pointer',
                                            '&:hover': { color: 'primary.dark' },
                                          }}
                                        >
                                          {file?.fileName || historyEntryTitle(entry, t)}
                                        </Link>
                                        {downloadingId === downloadId
                                          ? <CircularProgress size={14} aria-label={t('dataInput.history.downloading')} />
                                          : null}
                                      </Stack>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700, maxWidth: 320, overflowWrap: 'anywhere' }}
                                >
                                  {historyEntryTitle(entry, t)}
                                </Typography>
                              )}
                              {downloadError?.id === entry.id
                                || entry.files?.some((file) => downloadError?.id === `${entry.id}:${file.fileId}`) ? (
                                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.35 }}>
                                  {t('dataInput.history.downloadError')}
                                </Typography>
                              ) : null}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.35 }}
                              >
                                {formatHistoryDateTime(entry.createdAt, i18n.language)}
                              </Typography>
                              {Boolean(entry.fileName || entry.fileNames?.length) && (entry.note || showImportOutcome) && (
                                <Typography
                                  variant="caption"
                                  color={entry.status === 'FAILED' ? 'error.main' : 'text.secondary'}
                                  sx={{ display: 'block', mt: 0.35, maxWidth: 360 }}
                                >
                                  {showImportOutcome
                                    ? outcomeMessage
                                    : t(entry.noteKey || 'dataInput.history.importCompleted')}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: { xs: 'block', md: 'none' }, mt: 0.35 }}
                              >
                                {actor || EMPTY_VALUE}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <StatusChip status={entry.status} />
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, verticalAlign: 'top' }}>
                              <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                                {actor || EMPTY_VALUE}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, verticalAlign: 'top' }}>
                              <HistorySummary entry={entry} />
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ ...tableActionCellSx, verticalAlign: 'top' }}
                            >
                              <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                <Tooltip title={t('dataInput.history.viewDetails')}>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => viewDetails(entry)}
                                    startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                                    aria-label={t('dataInput.history.viewDetailsFor', {
                                      item: historyEntryTitle(entry, t),
                                    })}
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {t('dataInput.history.details')}
                                  </Button>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {visibleEntries.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {t('dataInput.history.noFilteredResults')}
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => {
                                  setStatusFilter(HISTORY_STATUS_ALL);
                                  resetTableContext();
                                }}
                              >
                                {t('dataInput.history.clearFilter')}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredEntries.length}
                  page={page}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  onPageChange={(_event, nextPage) => {
                    setPage(nextPage);
                    closeDetail();
                  }}
                  sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    flex: '0 0 auto',
                    '& .MuiTablePagination-toolbar': {
                      minHeight: 52,
                      px: { xs: 1, sm: 2 },
                    },
                    '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
                    '& .MuiTablePagination-displayedRows': {
                      flex: { xs: 1, sm: 'initial' },
                      ml: { xs: 0 },
                      whiteSpace: 'nowrap',
                    },
                    '& .MuiTablePagination-actions': { ml: { xs: 0.5, sm: 2.5 } },
                  }}
                />
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleClose}>{t('common.close')}</Button>
        </DialogActions>
      </AppDialog>
      <HistoryDetailDialog
        open={detailOpen}
        entry={selectedEntry}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={closeDetail}
        onExited={resetDetailPayload}
        onRetry={retryDetail}
      />
    </>
  );
}
